import { type ReactNode } from 'react'
import { Menu, Popover } from '@systemeio/components'
import { ChevronDownIcon } from '@systemeio/icons'
import { cn } from 'cn'
import { useTranslation } from 'next-i18next/pages'
import { LocaleEnum, localeNames } from 'shared/enums/locale-enum'
import { useFloatHover } from 'shared/hooks/use-float-hover'
import { useLanguageSwitcher } from 'shared/hooks/use-language-switcher'
import { getCalculatedLanguage } from 'shared/utils/get-calculated-language'
import useUser from '../hooks/use-user'

export function PublicDashboardLanguageSwitcher(): ReactNode {
  const { i18n } = useTranslation()
  const { user } = useUser()
  const { close, delayClose, delayOpen, open, show } = useFloatHover()
  const { handleChangeLanguage, isChangingLanguage } = useLanguageSwitcher()
  if (!i18n) {
    return <div>loading...</div>
  }

  return user ? (
    <div className="flex h-full items-center font-inter">
      <Popover.Root
        open={show}
        onOpenChange={(next: boolean) => {
          if (next) {
            open()
          } else {
            close()
          }
        }}
      >
        <Popover.Trigger
          data-testid="public-dashboard-language-switcher-trigger"
          onMouseEnter={delayOpen}
          onMouseLeave={delayClose}
          className="flex h-full items-center justify-around gap-2.5 text-black outline-hidden focus-visible:focus-ring-primary"
        >
          <div
            className={cn(
              'w-5 text-sm font-semibold text-black',
              isChangingLanguage && 'shimmer-loading',
            )}
          >
            {i18n && i18n.language ? getCalculatedLanguage(i18n.language) : ''}
          </div>
          <ChevronDownIcon className="size-5 text-black" aria-hidden="true" />
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Positioner side="bottom" sideOffset={8}>
            <Popover.Popup className="max-w-none">
              <Popover.Arrow />
              <div
                data-testid="public-dashboard-language-switcher-panel"
                onMouseEnter={delayOpen}
                onMouseLeave={delayClose}
                className="flex flex-col py-4 text-sm"
              >
                <ul>
                  {Object.keys(localeNames)
                    .filter(dashboardLocale => dashboardLocale !== i18n.language)
                    .map(dashboardLocale => (
                      <li key={dashboardLocale}>
                        <button
                          className="w-full cursor-pointer px-7 py-1 text-start text-text-tertiary hover:text-text-link focus:outline-hidden focus-visible:text-text-link"
                          onClick={async () =>
                            handleChangeLanguage(dashboardLocale as LocaleEnum, {
                              forceRouterUpdate: true,
                            })
                          }
                          type="button"
                        >
                          {localeNames[dashboardLocale as LocaleEnum]}
                        </button>
                      </li>
                    ))}
                </ul>
              </div>
            </Popover.Popup>
          </Popover.Positioner>
        </Popover.Portal>
      </Popover.Root>
    </div>
  ) : (
    <div className="h-6 w-11 animate-pulse rounded-xs bg-bg-tertiary" />
  )
}

export function MobilePublicLanguageSwitcher(): ReactNode {
  const { i18n } = useTranslation()
  const { user } = useUser()
  const { handleChangeLanguage, isChangingLanguage } = useLanguageSwitcher()

  if (!i18n) {
    return <div>loading...</div>
  }

  return (
    <Menu.Root>
      <Menu.Trigger
        data-testid="public-dashboard-mobile-language-switcher-trigger"
        className="relative flex items-center justify-between gap-2 px-4 py-2 font-inter text-text-secondary"
      >
        <span
          className={cn(
            'flex-1 text-start text-text-secondary capitalize',
            isChangingLanguage && 'shimmer-loading',
          )}
        >
          {user?.dashboardLocale}
        </span>
        <ChevronDownIcon className="size-3 rotate-180 text-text-primary" aria-hidden="true" />
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner side="top" align="end">
          <Menu.Popup className="max-w-none min-w-0" style={{ width: 'var(--anchor-width)' }}>
            <div className="flex flex-wrap py-1">
              {Object.keys(localeNames)
                .filter(dashboardLocale => dashboardLocale !== i18n.language)
                .map(dashboardLocale => (
                  <Menu.Item
                    key={dashboardLocale}
                    className="my-2 inline-flex w-auto px-4 py-1 text-text-secondary"
                    onClick={() => {
                      void handleChangeLanguage(dashboardLocale as LocaleEnum, {
                        forceRouterUpdate: true,
                      })
                    }}
                  >
                    {localeNames[dashboardLocale as LocaleEnum]}
                  </Menu.Item>
                ))}
              {Object.keys(localeNames).includes(i18n.language) && (
                <Menu.Item
                  className="my-2 inline-flex w-auto items-center px-4 py-1 text-text-link"
                  onClick={() => {
                    void handleChangeLanguage(i18n.language as LocaleEnum, {
                      forceRouterUpdate: true,
                    })
                  }}
                >
                  {localeNames[i18n.language as LocaleEnum]}
                  <ChevronDownIcon
                    className="ml-3 size-3 rotate-180 text-text-primary"
                    aria-hidden="true"
                  />
                </Menu.Item>
              )}
            </div>
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  )
}
